/**
 * Netzwerkprotokoll zwischen Client und Server.
 *
 * Der Server ist die alleinige Quelle der Wahrheit (Masterprompt Abschnitt 28).
 * Der Client sendet ausschliesslich ABSICHTEN ("ich moechte mich bewegen",
 * "ich moechte kaufen") - niemals fertige Ergebnisse.
 *
 * Stand v0.5: Verbindung, Latenzmessung, Skills, Zielauswahl, Wellen senden
 * und - nur in der Entwicklung - Dev-Aktionen.
 */

import type { MatchOutcome, PlayerSide } from './types.js';

/** Nachrichten, die der Client an den Server sendet. */
export const ClientMessage = {
  /** Latenzmessung. Der Server antwortet unveraendert mit der Client-Zeit. */
  Ping: 'ping',
  /** Wunsch, einen Skill zu wirken. Der Server prueft und entscheidet. */
  CastSkill: 'castSkill',
  /** Wunsch, einen bestimmten Gegner bevorzugt anzugreifen. */
  SelectTarget: 'selectTarget',
  /** Wunsch, eine Welle zum Gegner zu schicken. Der Server prueft und bezahlt. */
  SendWave: 'sendWave',
  /** Wunsch, das naechste Tier freizuschalten (Abschnitt 64). */
  UpgradeTier: 'upgradeTier',
  /** Wunsch, einen Skillpunkt in einen Grundskill zu stecken (Abschnitt 46). */
  SpendSkillPoint: 'spendSkillPoint',
  /** Wahl des Talents auf Level 8. Endgueltig - kein Respec im Match. */
  ChooseTalent: 'chooseTalent',
  /** Wahl des Capstone auf Level 10. Endgueltig - kein Respec im Match. */
  ChooseCapstone: 'chooseCapstone',
  /** Wunsch, ein Item zu kaufen oder auszubauen (Abschnitt 66). */
  BuyItem: 'buyItem',
  /** Wunsch, ein Item zu verkaufen. Bringt 65 % zurueck. */
  SellItem: 'sellItem',
  /** Wunsch, ein Consumable zu kaufen. Belegt keinen Slot. */
  BuyConsumable: 'buyConsumable',
  /** Wunsch, ein Consumable zu benutzen. */
  UseConsumable: 'useConsumable',
  /**
   * Aufgeben (Abschnitt 90).
   *
   * Anders als das Verlassen bleibt man dabei im Raum: Man sieht sein
   * Ergebnis und kann ein neues Match anbieten. Der Gegner gewinnt.
   */
  Surrender: 'surrender',
  /**
   * "Ich bin bereit" im Warteraum (Produktentscheidung vom 05.10.2026).
   *
   * Dieselbe Nachricht fuer beide Faelle, weil es dieselbe Frage ist:
   * vor dem ersten Match und nach einem beendeten Match ("Rematch").
   * Erst wenn BEIDE bereit sind, geht es los. Nochmal gedrueckt nimmt
   * die Zustimmung wieder zurueck - solange der Countdown nicht laeuft.
   */
  LobbyReady: 'lobbyReady',
  /**
   * Wahl des Linebound IN DER LOBBY (Produktentscheidung vom 13.10.2026).
   *
   * Vorher reiste der Wunsch beim Beitreten mit und stand danach fest. Jetzt
   * waehlt man dort, wo man auch den Gegner sieht - und kann es sich
   * anders ueberlegen, solange die Uhr laeuft.
   */
  SelectHero: 'selectHero',
  /**
   * Eine der festen Kurznachrichten im Warteraum.
   *
   * Uebertragen wird ausschliesslich eine KENNUNG, nie getippter Text.
   * Damit kann gar kein personenbezogener Inhalt entstehen - auch nicht
   * versehentlich im Protokoll oder im Matcharchiv (Abschnitt 27).
   */
  LobbySignal: 'lobbySignal',
  /**
   * Dev-/Cheat-Aktion (Masterprompt Abschnitt 96).
   * Der Server fuehrt sie NUR im Dev-Modus aus und ignoriert sie in Produktion.
   */
  DevAction: 'devAction',
} as const;

export type ClientMessageType = (typeof ClientMessage)[keyof typeof ClientMessage];

/** Nachrichten, die der Server an den Client sendet. */
export const ServerMessage = {
  /** Einmalige Begruessung direkt nach erfolgreichem Beitritt. */
  Welcome: 'welcome',
  /** Antwort auf Ping. */
  Pong: 'pong',
  /** Match wurde beendet. */
  MatchEnded: 'matchEnded',
  /** Eine eigene Welle wurde bezahlt und ist unterwegs zum Gegner. */
  WaveSent: 'waveSent',
  /** Das naechste Tier wurde bezahlt und freigeschaltet. */
  TierUpgraded: 'tierUpgraded',
  /** Ein Wunsch des Clients wurde abgelehnt - mit Grund fuer die Anzeige. */
  ActionRejected: 'actionRejected',
  /**
   * Jemand hat im Warteraum eine Kurznachricht geschickt.
   *
   * Wird nur weitergereicht, nirgends gespeichert - weder im Zustand des
   * Raumes noch im Protokoll. Eine verpasste Nachricht ist weg, und das
   * ist fuer einen Warteraum genau richtig.
   */
  LobbySignal: 'lobbySignal',
} as const;

export type ServerMessageType = (typeof ServerMessage)[keyof typeof ServerMessage];

/**
 * Die festen Kurznachrichten des Warteraums
 * (Produktentscheidung vom 05.10.2026).
 *
 * Bewusst eine kurze, feste Liste statt freiem Text:
 * - Nichts zu moderieren, nichts zu uebersetzen, nichts zu speichern.
 * - Auf dem Handy tippt ohnehin niemand gern.
 * - Freien Text kann man spaeter nachlegen; zuruecknehmen kann man ihn nicht.
 */
export const LOBBY_SIGNALS = [
  'ready_question',
  'one_moment',
  'good_luck',
  'rematch',
  'lets_go',
] as const;

export type LobbySignal = (typeof LOBBY_SIGNALS)[number];

/** Ist das eine bekannte Kurznachricht? Alles andere wird verworfen. */
export function isLobbySignal(wert: unknown): wert is LobbySignal {
  return typeof wert === 'string' && (LOBBY_SIGNALS as readonly string[]).includes(wert);
}

/** Nutzlast von ClientMessage.LobbySignal und ServerMessage.LobbySignal. */
export interface LobbySignalPayload {
  signal: LobbySignal;
  /** Wer sie geschickt hat - beim Senden leer, der Server traegt ihn ein. */
  sessionId?: string;
}

/** Nutzlast von ClientMessage.Ping. */
export interface PingPayload {
  /** Zeitstempel des Clients in Millisekunden (performance.now()). */
  clientTime: number;
}

/**
 * Nutzlast von ClientMessage.CastSkill.
 *
 * Auch hier schickt der Client nur eine ABSICHT: "ich moechte Skill 2 in diese
 * Richtung wirken". Ob genug Mana da ist, ob der Skill bereit ist und wen er
 * trifft, entscheidet allein der Server. Ein Zielpunkt ausserhalb der Reichweite
 * wird serverseitig gekuerzt, nicht abgelehnt.
 */
export interface CastSkillPayload {
  /** 1 bis 3 sind die Grundskills, 4 ist das Ultimate. */
  slot: number;
  /** Zielpunkt auf der 2D-Simulationsebene. */
  aimX: number;
  aimY: number;
}

/**
 * Nutzlast von ClientMessage.SelectTarget.
 *
 * Ein leerer Text hebt die Auswahl wieder auf - dann greift der Hero wieder den
 * naechstgelegenen Gegner an (Masterprompt Abschnitt 44).
 */
export interface SelectTargetPayload {
  creepId: string;
}

/**
 * Nutzlast von ClientMessage.SendWave.
 *
 * Der Client schickt nur die WUNSCHWELLE aus dem Army Composer. Preis,
 * Income-Zuwachs, Tier und Army Points prueft und berechnet ausschliesslich der
 * Server.
 */
export interface SendWavePayload {
  front: string[];
  center: string[];
  back: string[];
}

/** Dev-Aktionen, die der Server im Dev-Modus annimmt. */
export const DevActionType = {
  AddGold100: 'add_gold_100',
  AddGold1000: 'add_gold_1000',
  AddIncome50: 'add_income_50',
  /** Erfahrung, um Level und Freischaltungen zu testen. */
  AddXp250: 'add_xp_250',
  /**
   * Creeps fuer das Dev-Menue - in BEIDE Richtungen dieselben vier
   * (Robin, 12.10.2026).
   *
   * "spawn" heisst: ein GEGNER erscheint in der eigenen Lane und laeuft
   * auf einen zu. "send" heisst: ein EIGENER Creep marschiert zum Gegner -
   * ohne Gold, ohne Wellentakt und ohne Armeepunkte.
   *
   * Dass es beide Richtungen fuer dieselben Creeps gibt, ist eine Regel
   * und kein Zufall: Ein Test in MatchSimulation.test.ts haelt sie fest.
   */
  SpawnMinion: 'spawn_minion',
  SpawnBrute: 'spawn_brute',
  SpawnRunner: 'spawn_runner',
  SpawnRaider: 'spawn_raider',
  SendMinion: 'send_minion',
  SendBrute: 'send_brute',
  SendRunner: 'send_runner',
  SendRaider: 'send_raider',
} as const;

export type DevActionTypeValue = (typeof DevActionType)[keyof typeof DevActionType];

/** Nutzlast von ClientMessage.SpendSkillPoint. */
export interface SpendSkillPointPayload {
  /** Grundskill, der verbessert werden soll: 1, 2 oder 3. */
  slot: number;
}

/** Nutzlast von ClientMessage.SelectHero. */
export interface SelectHeroPayload {
  /** Der gewuenschte Linebound. */
  heroId: string;
}

/** Nutzlast von ClientMessage.ChooseTalent und ChooseCapstone. */
export interface HeroChoicePayload {
  /** ID der gewaehlten Moeglichkeit. */
  choiceId: string;
}

/** Nutzlast der Item-Nachrichten. */
export interface ItemPayload {
  /** ID des Items oder des Consumables. */
  itemId: string;
}

/** Nutzlast von ClientMessage.DevAction. */
export interface DevActionPayload {
  action: DevActionTypeValue;
}

/**
 * Nutzlast von ServerMessage.WaveSent.
 *
 * Enthaelt auch die bestaetigte Welle selbst. Der Client fuehrt damit seinen
 * Verlauf ("Last Wave", "History") - und zwar nur mit Wellen, die der Server
 * tatsaechlich angenommen hat.
 */
export interface WaveSentPayload {
  /** Eindeutige ID der Welle im Match. */
  waveId: string;
  /** Die angenommene Welle, Reihe fuer Reihe. */
  front: string[];
  center: string[];
  back: string[];
  /** Anzahl der Creeps in der Welle. */
  creepCount: number;
  /** Bezahlter Preis. */
  cost: number;
  /** Dauerhafter Income-Zuwachs, gilt bereits fuer den naechsten Tick. */
  incomeGain: number;
  /** Sekunden, bis die Welle beim Gegner erscheint. */
  arrivesInSeconds: number;
}

/** Nutzlast von ServerMessage.TierUpgraded. */
export interface TierUpgradedPayload {
  tier: number;
  cost: number;
}

/** Gruende, aus denen der Server einen Wunsch ablehnt. */
export const RejectReason = {
  NotEnoughGold: 'not_enough_gold',
  /**
   * Das Match steht gerade still, weil jemand die Verbindung verloren hat.
   *
   * In dieser Zeit geht gar nichts - weder beim Abwesenden noch bei dem, der
   * noch da ist (Produktentscheidung vom 25.09.2026).
   */
  MatchPaused: 'match_paused',
  MatchNotRunning: 'match_not_running',
  InvalidWave: 'invalid_wave',
  NoOpponent: 'no_opponent',
  /** Tier IV ist bereits erreicht - hoeher geht es nicht. */
  MaxTier: 'max_tier',
  /** Hoechste Stufe erreicht - beim Item-Ausbau. */
  MaxLevel: 'max_level',
  /** Kein freier Skillpunkt vorhanden. */
  NoSkillPoints: 'no_skill_points',
  /** Dieser Skill ist bereits voll ausgebaut. */
  SkillMaxed: 'already_maxed',
  /** Gerade steht keine Wahl an, oder sie ist schon getroffen. */
  NoChoicePending: 'no_choice_pending',
  /** Unbekannte Wahl oder unbekannter Skill. */
  UnknownChoice: 'unknown_choice',
  /** Alle vier Itemslots sind belegt. */
  NoFreeSlot: 'no_free_slot',
  /** Dieses Item liegt schon im Slot. */
  AlreadyOwned: 'already_owned',
  /** Dieses Item ist nicht im Besitz. */
  NotOwned: 'not_owned',
  /** Keine Portion mehr vorraetig, oder das Fass ist voll. */
  NoCharges: 'no_charges',
  /** Der Schluck klingt noch ab. */
  OnCooldown: 'on_cooldown',
  /** Die Wartezeit zwischen zwei Wellen laeuft noch. */
  SendCooldown: 'send_cooldown',
  /** In der gegnerischen Lane ist kein Platz mehr. */
  LaneFull: 'lane_full',
} as const;

export type RejectReasonType = (typeof RejectReason)[keyof typeof RejectReason];

/** Nutzlast von ServerMessage.ActionRejected. */
export interface ActionRejectedPayload {
  /** Welcher Wunsch abgelehnt wurde, z. B. sendWave. */
  action: string;
  reason: RejectReasonType;
}

/** Nutzlast von ServerMessage.Pong. */
export interface PongPayload {
  /** Unveraenderter Zeitstempel aus dem Ping. */
  clientTime: number;
  /** Serverzeit in Millisekunden beim Beantworten. */
  serverTime: number;
}

/** Nutzlast von ServerMessage.Welcome. */
export interface WelcomePayload {
  /** Eindeutige Match-ID, taucht auch in allen Serverlogs auf (Abschnitt 91). */
  matchId: string;
  /** Die Seite, die diesem Spieler zugewiesen wurde. */
  side: PlayerSide;
  /** Protokollversion des Servers. */
  protocolVersion: number;
  /** Simulationstakte pro Sekunde, mit denen der Server rechnet. */
  tickRate: number;
  /**
   * True, wenn der Server Dev-Aktionen annimmt.
   * In Produktion immer false - dann blendet der Client das Dev-Menue aus.
   */
  devMode: boolean;
}

/** Nutzlast von ServerMessage.MatchEnded. */
/**
 * Zahlen eines Spielers fuer den Ergebnisbildschirm (Abschnitt 92).
 *
 * Bewusst eine Auswahl: Aufgehoben wird auf dem Server deutlich mehr, gezeigt
 * wird nur, was nach einem Match wirklich interessiert (Produktentscheidung
 * vom 25.09.2026). Nach dem Matchende duerfen beide Seiten alles sehen - im
 * Match bleibt Gold geheim, danach hat Geheimhaltung keinen Zweck mehr.
 */
export interface MatchStatsPayload {
  sessionId: string;
  heroId: string;
  /** Ist das der Testgegner? */
  isBot: boolean;
  /**
   * Verbleibende Anchor-HP.
   *
   * Der angerichtete und der erlittene Schaden standen hier frueher als
   * eigene Zeilen. Sie sind am 05.10.2026 entfallen: Der Anchor startet
   * bei 100, der Restwert sagt dasselbe und ist leichter zu lesen.
   */
  portalHp: number;
  creepsSent: number;
  wavesSent: number;
  /** Creeps, die der Hero selbst erledigt hat. */
  killsByHero: number;
  /**
   * Creeps, die die eigene ARMEE erledigt hat (ab 08.10.2026).
   *
   * Auf der gemeinsamen Lane toeten ueberwiegend Creeps andere Creeps
   * (GAME_DESIGN Abschnitt 5c). Die Zahl geht ins Matcharchiv - ob sie auch
   * auf dem Ergebnisbildschirm auftaucht, ist noch offen.
   */
  killsByArmy: number;
  /**
   * Creep-Tode, bei denen der eigene Hero an der Front stand (ab 09.10.2026).
   *
   * Seit GAME_DESIGN Abschnitt 5c haengen Gold und Erfahrung genau daran.
   * Die Zahl geht ins Matcharchiv - nur so laesst sich spaeter nachsehen,
   * ob die Regel im Spiel ueberhaupt greift.
   */
  killsMitPraesenz: number;
  /**
   * Leben, das Heiler den eigenen gesendeten Creeps geschenkt haben
   * (ab 09.10.2026) - und wo diese Creeps gestorben sind.
   *
   * Beides gemessen fuer die Frage, warum Angriffe nicht durchkommen.
   */
  healedHp: number;
  todesFortschritt: number;
  todesZaehler: number;
  /**
   * Income am Rundenende (Produktentscheidung vom 05.10.2026).
   *
   * Die Zahl, an der die ganze Wirtschaft haengt - und sie stand bisher
   * nirgends auf dem Bildschirm.
   */
  income: number;
  goldEarned: number;
  goldSpent: number;
  /** Hoechstes erreichtes Tier und die Kampfzeit dazu (null, wenn Tier I). */
  highestTier: number;
  highestTierAtSeconds: number | null;
  level: number;
}

export interface MatchEndedPayload {
  outcome: MatchOutcome;
  /** Matchdauer in Sekunden. */
  durationSeconds: number;
  /** Warum das Match zu Ende ist. */
  reason: 'portal' | 'abandoned' | 'surrender';
  /** Die eigenen Zahlen. */
  own: MatchStatsPayload;
  /** Die des Gegners - null, wenn niemand mehr da war. */
  opponent: MatchStatsPayload | null;
}

/** Gruende, aus denen der Server einen Beitritt ablehnen kann. */
export const JoinRejectReason = {
  ProtocolMismatch: 'protocol_mismatch',
  MatchFull: 'match_full',
} as const;

export type JoinRejectReasonType = (typeof JoinRejectReason)[keyof typeof JoinRejectReason];
